/**
 * Traffic View - Website traffic analytics tab
 * Shows visitor stats, pageviews, bounce rate, top pages, sources,
 * plus insights: source efficiency, bounce rate analysis, session duration
 */
import { COLORS } from './shared/chartTheme.js';
import { TimeSeriesChart, HorizontalBarChart } from './shared/ChartComponents.jsx';
import { formatNumber } from './shared/formatters.js';
import { Icons } from './shared/Icons.jsx';
import { KPICard } from './shared/KPICard.jsx';

// External dashboard URL
const PLAUSIBLE_URL = 'https://analytics.batemo.com/insights.batemo.com';

/**
 * Source Efficiency — horizontal bars showing top traffic sources ranked by visitors
 */
function TrafficSourceEfficiency({ data }) {
  const sources = data?.plausible_data?.top_sources || [];
  if (!sources.length) return null;

  const maxVisitors = Math.max(...sources.map((s) => s.visitors || 0), 1);

  return (
    <div className="analytics-insight-section">
      <h3 className="analytics-insight-section-title">Source Efficiency</h3>
      <div className="traffic-source-efficiency">
        {sources.slice(0, 5).map((s) => (
          <div className="traffic-source-row" key={s.source || 'Direct'}>
            <span className="traffic-source-name">{s.source || 'Direct'}</span>
            <div className="traffic-source-bar-wrapper">
              <div
                className="traffic-source-bar"
                style={{ width: `${(s.visitors / maxVisitors) * 100}%` }}
              />
            </div>
            <span className="traffic-source-count">{formatNumber(s.visitors)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Bounce Rate Insight — severity-based card highlighting bounce rate health
 */
function BounceRateInsight({ data }) {
  const bounceRate = data?.plausible_data?.bounce_rate;
  if (bounceRate == null) return null;

  const severity = bounceRate > 60 ? 'critical' : bounceRate > 40 ? 'warning' : 'success';
  const colors = { critical: '#ef4444', warning: '#f59e0b', success: '#10b981' };

  return (
    <div className="analytics-insight-card" style={{ borderLeft: `3px solid ${colors[severity]}` }}>
      <div className="insight-card-header">
        <span className="insight-card-title">Bounce Rate: {bounceRate}%</span>
      </div>
      <p className="insight-card-description">
        {bounceRate > 60
          ? 'High bounce rate — visitors are leaving the page quickly. Review the landing page and content.'
          : bounceRate > 40
            ? 'Moderate bounce rate — there is room to optimize landing pages.'
            : 'Healthy bounce rate — visitors are engaging well with the content.'}
      </p>
    </div>
  );
}

/**
 * Session Duration Insight — flags short visits
 */
function SessionDurationInsight({ data }) {
  const duration = data?.plausible_data?.visit_duration;
  if (!duration && duration !== 0) return null;

  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  const severity = duration < 30 ? 'critical' : duration < 120 ? 'warning' : 'success';
  const colors = { critical: '#ef4444', warning: '#f59e0b', success: '#10b981' };

  return (
    <div className="analytics-insight-card" style={{ borderLeft: `3px solid ${colors[severity]}` }}>
      <div className="insight-card-header">
        <span className="insight-card-title">
          Avg. Visit Duration: {minutes}:{String(seconds).padStart(2, '0')}
        </span>
      </div>
      <p className="insight-card-description">
        {duration < 30
          ? 'Very short sessions — visitors may not be finding what they are looking for. Check content relevance and page speed.'
          : duration < 120
            ? 'Moderate session duration — potential for deeper engagement through better internal linking and more relevant content.'
            : 'Good session duration — visitors are engaging intensively with the content.'}
      </p>
    </div>
  );
}

export function AnalyticsTrafficView({ data, periodDays }) {
  const plausible = data.plausible_data;

  return (
    <div className="analytics-traffic-view">
      <div className="analytics-kpi-grid">
        <KPICard
          icon={Icons.users}
          label="Visitors"
          value={formatNumber(plausible?.visitors)}
          color="cyan"
        />
        <KPICard
          icon={Icons.chart}
          label="Page views"
          value={formatNumber(plausible?.pageviews)}
          color="orange"
        />
        <KPICard
          icon={Icons.alertTriangle}
          label="Bounce Rate"
          value={`${plausible?.bounce_rate || 0}%`}
          color={plausible?.bounce_rate > 60 ? 'red' : 'green'}
        />
        <KPICard
          icon={Icons.clock}
          label="Avg. Visit Duration"
          value={`${Math.floor((plausible?.visit_duration || 0) / 60)}:${String((plausible?.visit_duration || 0) % 60).padStart(2, '0')}`}
          sublabel="Minutes"
          color="purple"
        />
      </div>

      {/* Insights row */}
      <div className="analytics-traffic-insights">
        <TrafficSourceEfficiency data={data} />
        <BounceRateInsight data={data} />
        <SessionDurationInsight data={data} />
      </div>

      {/* Traffic Charts */}
      <div className="analytics-charts-row">
        <TimeSeriesChart
          title="Visitors & Page views"
          data={plausible?.timeseries || []}
          xKey="date"
          series={[
            { name: 'Visitors', key: 'visitors', color: COLORS.cyan, area: true },
            { name: 'Page views', key: 'pageviews', color: COLORS.orange },
          ]}
          height={300}
          infoKey="trafficTrend"
        />
        <TimeSeriesChart
          title="Bounce Rate Trend"
          data={plausible?.timeseries || []}
          xKey="date"
          series={[{ name: 'Bounce Rate %', key: 'bounce_rate', color: COLORS.red }]}
          height={300}
          infoKey="bounceRateTrend"
        />
      </div>

      <div className="analytics-charts-row">
        <HorizontalBarChart
          title="Top Pages"
          data={(plausible?.top_pages || []).slice(0, 8).map((p) => [p.page, p.visitors])}
          color={COLORS.cyan}
          height={280}
          infoKey="topSeiten"
        />
        <HorizontalBarChart
          title="Traffic Sources"
          data={(plausible?.top_sources || [])
            .slice(0, 8)
            .map((s) => [s.source || 'Direct', s.visitors])}
          color={COLORS.purple}
          height={280}
          infoKey="trafficQuellen"
        />
      </div>

      {/* Top Pages Table */}
      {plausible?.top_pages && (
        <div className="analytics-table-section">
          <h3>Top Pages</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Visitors</th>
              </tr>
            </thead>
            <tbody>
              {plausible.top_pages.map((page, idx) => (
                <tr key={idx}>
                  <td className="endpoint-cell">{page.page}</td>
                  <td className="count-cell">{formatNumber(page.visitors)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Sources Table */}
      {plausible?.top_sources && (
        <div className="analytics-table-section">
          <h3>Traffic Sources</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Visitors</th>
              </tr>
            </thead>
            <tbody>
              {plausible.top_sources.map((source, idx) => (
                <tr key={idx}>
                  <td>{source.source || 'Direct / None'}</td>
                  <td className="count-cell">{formatNumber(source.visitors)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="analytics-external-links">
        <a
          href={PLAUSIBLE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="external-link-card"
        >
          <span className="external-link-icon">{Icons.users}</span>
          <div>
            <span className="external-link-title">Open Plausible</span>
            <span className="external-link-desc">Full traffic dashboard</span>
          </div>
          <span className="external-link-arrow">{Icons.externalLink}</span>
        </a>
      </div>
    </div>
  );
}
